import { useMemo } from 'react';
import { Box, Typography, Table, TableBody, TableCell, TableHead, TableRow, Paper, Button, Avatar } from '@mui/material';
import { Event, QueryItemTypesWithLocationsResponse } from '../schema';
import CreateOperationDialog from './CreateOperationDialog';
import { useState } from 'react';

interface Props {
    event: Event;
    itemTypesData: QueryItemTypesWithLocationsResponse | undefined;
}

export default function EventInventorySummary({ event, itemTypesData }: Props) {
    const [createDialogOpen, setCreateDialogOpen] = useState(false);

    // Get all item locations for this event
    const eventItemLocations = useMemo(() => {
        const locations: Array<{
            id: string;
            itemTypeId: string;
            itemTypeName: string;
            itemTypePhotoUrl?: string;
            categoryName?: string;
            quantity: number;
        }> = [];

        if (itemTypesData?.itemTypes) {
            for (const itemType of itemTypesData.itemTypes) {
                const itemLocations = (itemType as any).itemLocations || [];
                for (const location of itemLocations) {
                    if (location.event?.id === event.id && (location.quantity || 0) > 0) {
                        locations.push({
                            id: location.id,
                            itemTypeId: itemType.id,
                            itemTypeName: itemType.name || 'Unknown',
                            itemTypePhotoUrl: itemType.photoUrl,
                            categoryName: itemType.category?.name,
                            quantity: location.quantity || 0,
                        });
                    }
                }
            }
        }

        // Sort by category name, then by item name
        locations.sort((a, b) => {
            const categoryCompare = (a.categoryName || '').localeCompare(b.categoryName || '');
            if (categoryCompare !== 0) return categoryCompare;
            return a.itemTypeName.localeCompare(b.itemTypeName);
        });

        return locations;
    }, [event.id, itemTypesData]);

    if (eventItemLocations.length === 0) {
        return (
            <Box sx={{ textAlign: 'center', py: 2 }}>
                <Typography variant="body2" color="text.secondary">
                    No items at this event
                </Typography>
            </Box>
        );
    }

    return (
        <>
            <Paper sx={{ overflowX: 'auto', boxShadow: 'none', bgcolor: 'transparent' }}>
                <Table size="small">
                    <TableHead>
                        <TableRow>
                            <TableCell sx={{ width: 60 }}>Photo</TableCell>
                            <TableCell>Name</TableCell>
                            <TableCell>Category</TableCell>
                            <TableCell align="right" sx={{ fontWeight: 600 }}>
                                Quantity
                            </TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {eventItemLocations.map(location => (
                            <TableRow key={location.id}>
                                <TableCell>
                                    {location.itemTypePhotoUrl ? (
                                        <Avatar
                                            src={location.itemTypePhotoUrl}
                                            alt={location.itemTypeName}
                                            sx={{ width: 32, height: 32 }}
                                        />
                                    ) : (
                                        <Avatar sx={{ width: 32, height: 32, bgcolor: 'grey.300' }}>
                                            {location.itemTypeName.charAt(0).toUpperCase()}
                                        </Avatar>
                                    )}
                                </TableCell>
                                <TableCell>
                                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                        {location.itemTypeName}
                                    </Typography>
                                </TableCell>
                                <TableCell>
                                    {location.categoryName && (
                                        <Typography variant="caption" color="text.secondary">
                                            {location.categoryName}
                                        </Typography>
                                    )}
                                </TableCell>
                                <TableCell align="right">
                                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                        {location.quantity}
                                    </Typography>
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </Paper>

            {/* Quick Action */}
            <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end' }}>
                <Button variant="contained" size="small" onClick={() => setCreateDialogOpen(true)}>
                    Create Packing Operation to Retrieve Items
                </Button>
            </Box>

            {/* Create Operation Dialog */}
            <CreateOperationDialog open={createDialogOpen} onClose={() => setCreateDialogOpen(false)} initialSourceEventIds={[event.id]} />
        </>
    );
}
